import cx from 'classnames'
import {
  ElementType,
  forwardRef,
  HTMLAttributes,
  Ref,
} from 'react'
import styles from './Stack.module.css'

type Spacing = 0 | 0.5 | 1 | 1.5 | 2 | 3 | 4 | 5 | 6 | 8

type Direction =
  | 'row'
  | 'row-reverse'
  | 'column'
  | 'column-reverse'

type AlignItems =
  | 'center'
  | 'flex-start'
  | 'flex-end'
  | 'stretch'
  | 'baseline'

type JustifyContent =
  | 'center'
  | 'flex-start'
  | 'flex-end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'

type FlexWrap = 'nowrap' | 'wrap' | 'wrap-reverse'

export interface StackProps extends HTMLAttributes<HTMLElement> {
  direction?: Direction
  spacing?: Spacing
  gap?: Spacing
  alignItems?: AlignItems
  justifyContent?: JustifyContent
  flexWrap?: FlexWrap
  component?: ElementType
}

const SPACING_UNIT = 8

function spacingToPx(value: Spacing): string {
  return `${value * SPACING_UNIT}px`
}

const alignItemsMap: Record<string, string> = {
  center: styles.alignItemsCenter,
  'flex-start': styles.alignItemsFlexStart,
  'flex-end': styles.alignItemsFlexEnd,
  stretch: styles.alignItemsStretch,
  baseline: styles.alignItemsBaseline,
}

const justifyContentMap: Record<string, string> = {
  center: styles.justifyContentCenter,
  'flex-start': styles.justifyContentFlexStart,
  'flex-end': styles.justifyContentFlexEnd,
  'space-between': styles.justifyContentSpaceBetween,
  'space-around': styles.justifyContentSpaceAround,
  'space-evenly': styles.justifyContentSpaceEvenly,
}

export const Stack = forwardRef(function Stack(
  {
    direction,
    spacing = 2,
    gap,
    alignItems,
    justifyContent,
    flexWrap,
    component: Component = 'div',
    className,
    style,
    children,
    ...rest
  }: StackProps,
  ref: Ref<HTMLElement>
) {
  const gapValue = gap ?? spacing
  const inlineStyle =
    gapValue != null && gapValue !== 0
      ? { ...style, gap: spacingToPx(gapValue) }
      : style

  return (
    <Component
      ref={ref}
      className={cx(
        styles.Stack,
        {
          [styles.isRow]: direction === 'row',
          [styles.isRowReverse]: direction === 'row-reverse',
          [styles.isColumnReverse]:
            direction === 'column-reverse',
          [styles.isWrap]: flexWrap === 'wrap',
          [styles.isWrapReverse]: flexWrap === 'wrap-reverse',
        },
        alignItems && alignItemsMap[alignItems],
        justifyContent && justifyContentMap[justifyContent],
        className
      )}
      style={inlineStyle}
      {...rest}
    >
      {children}
    </Component>
  )
})
